/**
 * A text layer's glyphs, laid out as lines an SVG <mask> can draw.
 *
 * A mask has to be SVG: CSS cannot clip to letters, and a <foreignObject> inside
 * a <mask> renders nothing in Chromium (probed — the masked element vanishes
 * entirely). So the glyphs are re-drawn as <text>, one element per visual line.
 *
 * The lines are MEASURED, not computed: a hidden probe styled exactly like the
 * real text box is laid out by the browser, and each line's box and string are
 * read back off it with a Range. Wrapping, alignment, padding, letter-spacing and
 * text-transform therefore match what the canvas shows for free — the alternative
 * is a second wrapping algorithm that drifts from the first one the day a font
 * changes.
 *
 * Costly enough to cache: one measurement per (text + styles + width), reused
 * until one of them changes.
 */
import { resolveFontStack } from '../panels/propertyControls';
import { ELEMENT_TYPES } from '../schema/documentSchema';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function textOf(el) {
    if (el?.content) return el.content;
    if (el?.type === ELEMENT_TYPES.PLACEHOLDER) return `{{${el.placeholderKey || 'field'}}}`;
    return '';
}

/** The text box inside the element — padding and border take their room first. */
function insets(styles = {}) {
    const pad = (side) => num(styles[`padding${side}`] ?? styles.padding);
    const bw = num(styles.borderWidth);
    return {
        left: pad('Left') + bw,
        top: pad('Top') + bw,
        right: pad('Right') + bw,
        bottom: pad('Bottom') + bw,
    };
}

let probe = null;
function getProbe() {
    if (typeof document === 'undefined') return null;
    if (probe && probe.isConnected) return probe;
    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:absolute;left:-99999px;top:0;visibility:hidden;'
        + 'pointer-events:none;margin:0;border:0;';
    document.body.appendChild(probe);
    return probe;
}

/**
 * The BASELINE of one line, read off the probe itself.
 *
 * A zero-sized inline-block sits on the baseline by definition, so inserting one
 * into the line and reading its top is the exact answer — for any script, at any
 * size, whichever face the browser actually chose. Deriving it from
 * `fontBoundingBoxAscent` instead was right for Latin and a pixel out for Bangla,
 * because canvas and layout do not have to pick the same fallback font.
 *
 * The catch is placement: a zero-width box inserted at the START of a wrapped
 * line stays on the line BEFORE it — it glues to the space that caused the break.
 * So the answer is checked against the line's own rect and retried one character
 * in, which is unambiguously inside the line.
 *
 * Inserting splits the text node, so the probe is refilled afterwards and every
 * caller re-reads it. Runs once per text, then cached.
 */
function baselineAt(probeEl, text, offset) {
    const node = probeEl.firstChild;
    if (!node || offset >= node.length) return null;
    const marker = document.createElement('span');
    marker.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    range.insertNode(marker);
    const top = marker.getBoundingClientRect().top;
    marker.remove();
    probeEl.textContent = text; // put the split node back together
    return top;
}

/** The baseline of the line whose first character sits in `rect`. */
function lineBaseline(probeEl, text, line, fontSize) {
    for (const offset of [line.start, line.start + 1]) {
        const top = baselineAt(probeEl, text, offset);
        // A baseline at or above the line's own top belongs to the line before it.
        if (top != null && top > line.rect.top) return top;
    }
    return line.rect.top + fontSize * 0.8;
}

/**
 * Split the probe's text into its VISUAL lines.
 *
 * Walked one character at a time with a Range: whenever the rect a character
 * lands in starts a new row, the line before it ended there. That is the only
 * way to learn where the browser broke a line — `getClientRects()` gives the
 * boxes but never says which characters are in them.
 */
function measureLines(node, text) {
    const range = document.createRange();
    const out = [];
    let lineStart = 0;
    let lineTop = null;
    let lineRect = null;

    const flush = (end) => {
        if (end <= lineStart || !lineRect) return;
        out.push({ text: text.slice(lineStart, end), start: lineStart, rect: lineRect });
    };

    for (let i = 0; i < text.length; i += 1) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const rects = range.getClientRects();
        const r = rects[rects.length - 1];
        // A newline character has no box of its own; the next character starts
        // the new row and is what tells us the break happened.
        if (!r || (!r.width && !r.height)) continue;
        if (lineTop === null || Math.abs(r.top - lineTop) > 1) {
            flush(i);
            lineStart = i;
            lineTop = r.top;
            lineRect = { top: r.top, left: r.left, right: r.right };
        } else {
            lineRect.left = Math.min(lineRect.left, r.left);
            lineRect.right = Math.max(lineRect.right, r.right);
        }
    }
    flush(text.length);
    return out;
}

const cache = new Map();
const CACHE_MAX = 120;

function cacheKey(el) {
    const s = el.styles || {};
    return JSON.stringify([
        textOf(el), el.width, el.height,
        s.fontFamily, s.fontVariant, s.fontSize, s.fontWeight, s.fontStyle,
        s.lineHeight, s.letterSpacing, s.textAlign, s.textTransform, s.whiteSpace,
        s.padding, s.paddingTop, s.paddingRight, s.paddingBottom, s.paddingLeft,
        s.borderWidth, s.verticalAlign,
    ]);
}

/**
 * The glyph lines of a text element, in ITS OWN coordinates (0,0 = the top-left
 * of its box), ready to be written as <text> elements.
 *
 * Returns null when there is nothing to draw — an empty text layer masks nothing,
 * and the caller falls back to the box rather than making the subject vanish.
 */
export function textMaskLines(el) {
    const text = textOf(el);
    if (!text.trim()) return null;
    const p = getProbe();
    if (!p) return null;

    const key = cacheKey(el);
    const hit = cache.get(key);
    if (hit) return hit;

    const styles = el.styles || {};
    const fontSize = num(styles.fontSize) || 16;
    const lineHeight = Number(styles.lineHeight ?? 1.3) || 1.3;
    const pad = insets(styles);
    const fontFamily = resolveFontStack(styles);
    const fontWeight = styles.fontWeight || 400;
    const fontStyle = styles.fontStyle || 'normal';

    const s = p.style;
    s.fontFamily = fontFamily;
    s.fontSize = `${fontSize}px`;
    s.fontWeight = String(fontWeight);
    s.fontStyle = fontStyle;
    s.lineHeight = String(lineHeight);
    s.letterSpacing = styles.letterSpacing != null ? `${styles.letterSpacing}px` : 'normal';
    s.textTransform = styles.textTransform || 'none';
    s.textAlign = styles.textAlign || 'left';
    s.whiteSpace = styles.whiteSpace || 'pre-wrap';
    s.wordBreak = 'normal';
    s.padding = '0';
    s.width = `${Math.max(1, num(el.width) - pad.left - pad.right)}px`;
    p.textContent = text;

    const node = p.firstChild;
    if (!node) return null;
    const box = p.getBoundingClientRect();
    // `textContent` applies the string as written; the browser paints it
    // transformed, so the measured slices are the SOURCE characters — which is
    // what <text> must be given, with the same transform applied by CSS.
    const measured = measureLines(node, text);
    if (!measured.length) return null;

    // Vertical alignment is on the element's box, not the probe's: the probe is
    // only as tall as its text, so the block is placed here. Its measured height
    // IS the text height — line count times line-height would re-derive a number
    // the browser has already worked out, rounding included.
    const textHeight = box.height;
    const boxInner = Math.max(0, num(el.height) - pad.top - pad.bottom);
    // Unclamped on purpose: the renderer aligns with `justify-content` on a flex
    // column, and flex overflow spills out of the box rather than sticking to the
    // top. A text taller than its box has to spill the same way here, or the mask
    // would sit lower than the letters it is meant to be.
    const vAlign = styles.verticalAlign || 'top';
    const vOffset = vAlign === 'middle' ? (boxInner - textHeight) / 2
        : vAlign === 'bottom' ? boxInner - textHeight
            : 0;

    // The transform is applied to the STRINGS here rather than left to CSS on the
    // <text>: `text-transform` on SVG text is not something to bet a mask on, and
    // every transform the editor offers is one character in, one character out —
    // so the measured slices still line up with what is drawn.
    const transform = styles.textTransform || 'none';
    const applyTransform = (t) => (
        transform === 'uppercase' ? t.toUpperCase()
            : transform === 'lowercase' ? t.toLowerCase()
                : transform === 'capitalize' ? t.replace(/(^|\s)(\S)/g, (m0, a, b) => a + b.toUpperCase())
                    : t
    );

    const lines = [];
    measured.forEach((m) => {
        const baseTop = lineBaseline(p, text, m, fontSize);
        lines.push({
            text: applyTransform(m.text),
            x: pad.left + (m.rect.left - box.left),
            y: pad.top + vOffset + (baseTop - box.top),
        });
    });
    if (!lines.length) return null;

    const result = {
        lines,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        letterSpacing: num(styles.letterSpacing),
    };

    if (cache.size > CACHE_MAX) cache.clear();
    cache.set(key, result);
    return result;
}
